import { doRequest } from '@r1-frontend/do-request';
import { IOfferCreateResponseDto, offerCreate } from '@r1-frontend/api-domru/full-buy/v2/agreement/offer-create';
import { IVerifiedAddressData } from '@r1-frontend/entities/ContactAddress/initialState';

import { getGaId } from '@r1-frontend/shared/helpers/getGaId';

import { getErrorMessage } from '~/src/features/full-buy/functions';
import { setAgreementData, setLoading, setStep } from '~/src/features/tariffBuilder/actions';
import { GA_CATEGORY } from '~/src/features/tariffBuilder/constants/analytics';
import { TCtxDispatch } from '~/src/features/tariffBuilder/Ctx';
import { getSummaryOfferInfo } from '~/src/features/tariffBuilder/helpers/getSummaryOfferInfo';
import { submitRequest } from '~/src/features/tariffBuilder/helpers/submitRequest';
import { transformEquipmentForRequest } from '~/src/features/tariffBuilder/helpers/transformer/transformEquipmentForRequest';
import { TInitialState } from '~/src/features/tariffBuilder/reducer';
import getSelectedEntities from '~/src/features/tariffBuilder/selectors/getSelectedEntities';
import getSummaryInfo from '~/src/features/tariffBuilder/selectors/getSummaryInfo';
import isBaseTvSelected from '~/src/features/tariffBuilder/selectors/isBaseTvSelected';
import { EEntity } from '~/src/features/tariffBuilder/types/EEntity';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';

const createAgreement = (
    providerId: number,
    domain: string,
    verifiedAddress: IVerifiedAddressData,
    csrf: string,
) => async(dispatch: TCtxDispatch, getState: () => TInitialState) => {
    const sendAnalytics = getAnalyticsSender(GA_CATEGORY);

    dispatch(setLoading(true));
    const state = getState();
    const selectedEquip = getSelectedEntities(state);
    const clientInfo = state.client;

    const {
        address,
        companyId,
    } = verifiedAddress;

    if (!address) {
        return;
    }
    const { street, house, flat } = address;
    const houseId = house?.value;

    const analyticsId = getGaId();

    if (!houseId || !flat) {
        return;
    }

    const createData = {
        address: {
            houseId: houseId,
            office: flat,
            companyId,
        },
        personalInformation: {
            clientName: clientInfo.name,
            clientPhone: clientInfo.phone,
        },
        tariff: {
            tariffId: Number(state.salePackageId),
        },
        items: selectedEquip.items,
        comment: getSummaryOfferInfo(selectedEquip.summary),
        analyticsId,
    };

    const respAgreement = await doRequest.fullBuy<IOfferCreateResponseDto>(
        offerCreate(providerId, createData),
    );

    /* Ошибка создания договора. Таймслоты пропускаем переходим к финальному шагу */
    if (!respAgreement.isSuccess) {
        dispatch(setAgreementData({
            agreementId: null,
            agreementNumber: null,
            isDouble: false,
            hasError: true,
            errorMessage: getErrorMessage({}),
        }));
        dispatch(setStep(ESteps.Final));
        sendAnalytics('create agreement', 'error');
        // Отправляем заявку на подключение
        const routers = selectedEquip.items.filter(el => el.type === EEntity.routers);
        const decoders = selectedEquip.items.filter(el => el.type === EEntity.decoders);
        submitRequest({
            hasTv: isBaseTvSelected(state),
            fio: clientInfo.name,
            phone: clientInfo.phone,
            domain,
            csrf,
            salePackageId: Number(state.salePackageId),
            routers: transformEquipmentForRequest(routers),
            decoders: transformEquipmentForRequest(decoders),
            street: street?.label,
            house: house?.label,
            flat,
        });
        return;
    }

    const { isDouble, fromCache } = respAgreement.payload;
    const agreementNumber = respAgreement.isSuccess ? Number(respAgreement.payload.agreementNumber) : null;
    const agreementId = respAgreement.isSuccess ? Number(respAgreement.payload.agreementId) : null;

    /* Создан двойной договор. Таймслоты пропускаем переходим к финальному шагу */
    if (Number(isDouble) || fromCache) {
        // TODO рефакторинг, не обрабатывается ситуация если fromCache = true, уточнить flow для fromCache = true
        dispatch(setAgreementData({
            agreementId: null,
            agreementNumber: null,
            isDouble: !!isDouble,
            hasError: true,
            errorMessage: getErrorMessage({ isDouble: !!Number(isDouble), isFromCache: !!fromCache }),
        }));

        dispatch(setStep(ESteps.Final));

        sendAnalytics('create agreement', 'double');
        return;
    }

    if (!agreementId) {
        throw new Error('FullBuy Error. Required agreementId!');
    }

    if (!agreementNumber) {
        throw new Error('FullBuy Error. Required agreementNumber!');
    }

    /* Договор создан без ошибок, переходим к тайм-слотам */
    dispatch(setAgreementData({
        agreementId,
        agreementNumber,
        isDouble: false,
        hasError: false,
        errorMessage: '',
    }));
    dispatch(setStep(ESteps.SelectVisitData));

    dispatch(setLoading(false));

    const summaryInfo = getSummaryInfo(state);

    sendAnalytics('create agreement', `success ${agreementNumber} tariffId: ${state.salePackageId} total amount: ${summaryInfo}`);
};

export default createAgreement;
